// frontend/src/features/livestock/LivestockTable.tsx

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Badge, Text, ActionIcon, Menu, Group, Popover, Stack, Avatar } from '@mantine/core';
import { IconDots, IconEdit, IconSkull, IconTrash, IconSettings, IconChevronUp, IconChevronDown, IconSelector, IconPhoto } from '@tabler/icons-react';
import { formatAge } from '@/utils/formatAge';
import type { Livestock, Currency } from '@/types/livestock';
import { CURRENCIES } from '@/types/livestock';
import { useColumnPreferences, type ColumnKey } from '@/hooks/useColumnPreferences';
import { DraggableColumnList } from './DraggableColumnList';

interface LivestockTableProps {
  livestock: Livestock[];
  tankId: string;
  typeFilter: string | null;
  onEdit: (item: Livestock) => void;
  onMarkDeceased?: (item: Livestock) => void;
  onDelete?: (item: Livestock) => void;
}

type SortField = 'name' | 'status' | 'age' | 'quantity' | 'vendor' | 'price' | 'acquisition_date' | 'tank_bred' | 'category' | 'subcategory';
type SortDirection = 'asc' | 'desc';

const STORAGE_KEY = 'livestock-table-columns';

interface ColumnOption {
  key: ColumnKey;
  label: string;
  shortLabel: string;
  coralOnly?: boolean;
}

const ALL_COLUMN_OPTIONS: ColumnOption[] = [
  { key: 'thumbnail', label: 'Photo', shortLabel: 'Photo' },
  { key: 'name', label: 'Name', shortLabel: 'Name' },
  { key: 'category', label: 'Category', shortLabel: 'Category', coralOnly: true },
  { key: 'subcategory', label: 'Subcategory', shortLabel: 'Subcategory', coralOnly: true },
  { key: 'price', label: 'Price', shortLabel: 'Price' },
  { key: 'acquisition_date', label: 'Acquisition Date', shortLabel: 'Acquired' },
  { key: 'status', label: 'Status', shortLabel: 'Status' },
  { key: 'age', label: 'Age', shortLabel: 'Age' },
  { key: 'quantity', label: 'Quantity', shortLabel: 'Qty' },
  { key: 'vendor', label: 'Vendor', shortLabel: 'Vendor' },
  { key: 'tank_bred', label: 'Tank Bred', shortLabel: 'Tank Bred' },
];

const DEFAULT_COLUMNS: ColumnKey[] = ['thumbnail', 'name', 'category', 'subcategory', 'status', 'age', 'vendor'];

const getStatusColor = (status: string) => {
  switch (status) {
    case 'alive': return 'green';
    case 'deceased': return 'red';
    case 'sold': return 'blue';
    default: return 'gray';
  }
};

const formatPrice = (price: number | null, currency: Currency): string => {
  if (price === null) return '-';
  const currencyInfo = CURRENCIES.find(c => c.code === currency);
  const symbol = currencyInfo?.symbol || currency;
  return `${symbol}${price.toFixed(2)}`;
};

const formatDate = (dateString: string): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

interface SortHeaderProps {
  label: string;
  field: SortField;
  currentSort: SortField | null;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
}

function SortHeader({ label, field, currentSort, sortDirection, onSort }: SortHeaderProps) {
  const isActive = currentSort === field;

  return (
    <Table.Th
      style={{ cursor: 'pointer', userSelect: 'none' }}
      onClick={() => onSort(field)}
    >
      <Group gap={4} wrap="nowrap">
        <span>{label}</span>
        {isActive ? (
          sortDirection === 'asc' ? (
            <IconChevronUp size={14} style={{ color: 'var(--mantine-primary-color-filled)' }} />
          ) : (
            <IconChevronDown size={14} style={{ color: 'var(--mantine-primary-color-filled)' }} />
          )
        ) : (
          <IconSelector size={14} style={{ opacity: 0.25, transition: 'opacity 150ms' }} />
        )}
      </Group>
    </Table.Th>
  );
}

export function LivestockTable({ livestock, tankId, typeFilter, onEdit, onMarkDeceased, onDelete }: LivestockTableProps) {
  const navigate = useNavigate();
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [configPopoverOpen, setConfigPopoverOpen] = useState(false);

  // Filter column options based on type filter (coral-only columns hidden for non-coral views)
  const columnOptions = useMemo(() =>
    ALL_COLUMN_OPTIONS.filter(col => !col.coralOnly || typeFilter === 'coral'),
    [typeFilter]
  );

  const availableColumnKeys = useMemo(() =>
    new Set(columnOptions.map(col => col.key)),
    [columnOptions]
  );

  const {
    columns,
    toggleColumn,
    reorderColumns,
    resetToDefaults,
  } = useColumnPreferences(STORAGE_KEY, DEFAULT_COLUMNS);

  // Intersect user's saved columns with currently available columns
  const effectiveColumns = useMemo(() =>
    columns.filter(key => availableColumnKeys.has(key)),
    [columns, availableColumnKeys]
  );

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedLivestock = useMemo(() => {
    if (!sortField) return livestock;

    return [...livestock].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'name':
          comparison = a.common_name.localeCompare(b.common_name);
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
        case 'age':
          // ISO date strings sort lexicographically
          comparison = (a.acquisition_date || '').localeCompare(b.acquisition_date || '');
          break;
        case 'quantity':
          comparison = a.quantity - b.quantity;
          break;
        case 'vendor':
          comparison = (a.vendor || '').localeCompare(b.vendor || '');
          break;
        case 'price':
          comparison = (a.price_paid || 0) - (b.price_paid || 0);
          break;
        case 'acquisition_date':
          // ISO date strings sort lexicographically
          comparison = (a.acquisition_date || '').localeCompare(b.acquisition_date || '');
          break;
        case 'tank_bred':
          comparison = (a.tank_bred ? 1 : 0) - (b.tank_bred ? 1 : 0);
          break;
        case 'category':
          comparison = (a.category || '').localeCompare(b.category || '');
          break;
        case 'subcategory':
          comparison = (a.subcategory || '').localeCompare(b.subcategory || '');
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [livestock, sortField, sortDirection]);

  const renderCell = (key: ColumnKey, item: Livestock) => {
    switch (key) {
      case 'thumbnail':
        return (
          <Table.Td key={key} style={{ width: 50 }}>
            <Avatar
              src={item.thumbnail_url}
              size={36}
              radius="sm"
              color="gray"
            >
              <IconPhoto size={18} />
            </Avatar>
          </Table.Td>
        );
      case 'name': {
        // In non-coral views: show category · subcategory under coral names, scientific_name under others
        // In coral view: just name (category/subcategory are separate columns)
        const isCoral = item.livestock_type === 'coral';
        const showCoralSubtitle = isCoral && typeFilter !== 'coral';
        const coralSubtitle = [item.category, item.subcategory].filter(Boolean).join(' · ');
        return (
          <Table.Td key={key}>
            <Text fw={500}>
              {item.common_name}
            </Text>
            {showCoralSubtitle && coralSubtitle && (
              <Text size="xs" c="dimmed">{coralSubtitle}</Text>
            )}
            {!isCoral && item.scientific_name && (
              <Text size="xs" c="dimmed" fs="italic">{item.scientific_name}</Text>
            )}
          </Table.Td>
        );
      }
      case 'category':
        return (
          <Table.Td key={key}>
            {item.category ? (
              <Badge size="sm" variant="light" color="blue">
                {item.category}
              </Badge>
            ) : (
              <Text size="sm" c="dimmed">-</Text>
            )}
          </Table.Td>
        );
      case 'subcategory':
        return (
          <Table.Td key={key}>
            {item.subcategory ? (
              <Badge size="sm" variant="light" color="cyan">
                {item.subcategory}
              </Badge>
            ) : (
              <Text size="sm" c="dimmed">-</Text>
            )}
          </Table.Td>
        );
      case 'price':
        return (
          <Table.Td key={key}>
            <Text size="sm">{formatPrice(item.price_paid, item.currency)}</Text>
          </Table.Td>
        );
      case 'acquisition_date':
        return (
          <Table.Td key={key}>
            <Text size="sm">{formatDate(item.acquisition_date)}</Text>
          </Table.Td>
        );
      case 'status':
        return (
          <Table.Td key={key}>
            <Badge color={getStatusColor(item.status)} size="sm">
              {item.status}
            </Badge>
          </Table.Td>
        );
      case 'age':
        return (
          <Table.Td key={key}>
            <Text size="sm">{formatAge(item.acquisition_date, item.deceased_date)}</Text>
          </Table.Td>
        );
      case 'quantity':
        return <Table.Td key={key}>{item.quantity}</Table.Td>;
      case 'vendor':
        return (
          <Table.Td key={key}>
            <Text size="sm" c="dimmed">{item.vendor || '-'}</Text>
          </Table.Td>
        );
      case 'tank_bred':
        return (
          <Table.Td key={key}>
            <Badge
              color={item.tank_bred ? 'teal' : 'gray'}
              variant={item.tank_bred ? 'filled' : 'outline'}
              size="sm"
            >
              {item.tank_bred ? 'Yes' : 'No'}
            </Badge>
          </Table.Td>
        );
      default:
        return null;
    }
  };

  return (
    <Stack gap="xs">
      <Group justify="flex-end">
        <Popover
          opened={configPopoverOpen}
          onChange={setConfigPopoverOpen}
          position="bottom-end"
          shadow="md"
        >
          <Popover.Target>
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={() => setConfigPopoverOpen(!configPopoverOpen)}
              title="Configure columns"
            >
              <IconSettings size={18} />
            </ActionIcon>
          </Popover.Target>
          <Popover.Dropdown>
            <DraggableColumnList
              columns={columns}
              allColumns={columnOptions}
              onToggle={toggleColumn}
              onReorder={reorderColumns}
              onReset={resetToDefaults}
            />
          </Popover.Dropdown>
        </Popover>
      </Group>

      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            {effectiveColumns.map(key => {
              if (key === 'thumbnail') {
                return <Table.Th key={key} style={{ width: 50 }}>Photo</Table.Th>;
              }
              const col = columnOptions.find(c => c.key === key);
              if (!col) return null;
              return (
                <SortHeader
                  key={key}
                  label={col.shortLabel}
                  field={key as SortField}
                  currentSort={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
              );
            })}
            <Table.Th style={{ width: 50 }}></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {sortedLivestock.map((item) => (
            <Table.Tr key={item.id} onClick={() => navigate(`/tanks/${tankId}/livestock/${item.id}`)} style={{ cursor: 'pointer' }}>
              {effectiveColumns.map(key => renderCell(key, item))}
              <Table.Td onClick={(e) => e.stopPropagation()}>
                <Menu shadow="md" width={150}>
                  <Menu.Target>
                    <ActionIcon variant="subtle" color="gray">
                      <IconDots size={16} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item leftSection={<IconEdit size={14} />} onClick={() => onEdit(item)}>
                      Edit
                    </Menu.Item>
                    {item.status === 'alive' && onMarkDeceased && (
                      <Menu.Item leftSection={<IconSkull size={14} />} color="red" onClick={() => onMarkDeceased(item)}>
                        Mark Deceased
                      </Menu.Item>
                    )}
                    {onDelete && (
                      <Menu.Item leftSection={<IconTrash size={14} />} color="red" onClick={() => onDelete(item)}>
                        Delete
                      </Menu.Item>
                    )}
                  </Menu.Dropdown>
                </Menu>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}
